import { msg } from '@lingui/core/macro';
import type { I18n, MessageDescriptor } from '@lingui/core';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface StepMessages {
  title?: MessageDescriptor;
  content?: MessageDescriptor;
  missingElementContent?: MessageDescriptor;
  skipLabel?: MessageDescriptor;
  pauseLabel?: MessageDescriptor;
}

interface TourMessages {
  name: MessageDescriptor;
  description: MessageDescriptor;
  steps: Record<string, StepMessages>;
}

// ---------------------------------------------------------------------------
// Registry — one msg() per translatable string, keyed by tourId / stepId.
// The `context` param adds msgctxt to PO files for DeepL disambiguation.
// ---------------------------------------------------------------------------

export const tourMessages: Record<string, TourMessages> = {
  // =======================================================================
  // ONBOARDING TOUR
  // =======================================================================
  onboarding: {
    name: msg({ message: `Welcome Tour`, context: `tour name: onboarding` }),
    description: msg({
      message: `Set up your household and learn the basics`,
      context: `tour description: onboarding`,
    }),
    steps: {
      // --- Web steps ---
      'sidebar-nav': {
        title: msg({ message: `Navigation`, context: `onboarding tour > sidebar-nav: step title` }),
        content: msg({
          message: `Here's your navigation. Each section has its own mini-tour for when you're ready to explore.`,
          context: `onboarding tour > sidebar-nav: step description`,
        }),
      },
      'lets-get-started': {
        title: msg({
          message: `Let's Get Started`,
          context: `onboarding tour > lets-get-started: step title`,
        }),
        content: msg({
          message: `We'll guide you through the key features to get you up and running.`,
          context: `onboarding tour > lets-get-started: step description`,
        }),
      },
      // --- Mobile steps ---
      welcome: {
        title: msg({
          message: `Welcome to TrackMyGroceries`,
          context: `onboarding tour > welcome: step title`,
        }),
        content: msg({
          message: `Let's get you set up in about 30 seconds.`,
          context: `onboarding tour > welcome: step description`,
        }),
      },
      'tab-overview': {
        title: msg({
          message: `Your Main Sections`,
          context: `onboarding tour > tab-overview: step title`,
        }),
        content: msg({
          message: `Use these tabs to navigate between your dashboard, pantry, meal prep, and more. Next up: setting up your household.`,
          context: `onboarding tour > tab-overview: step description`,
        }),
      },
    },
  },

  // =======================================================================
  // RECEIPT SCANNING TOUR
  // =======================================================================
  'receipt-scanning': {
    name: msg({
      message: `Receipt Scanning Tour`,
      context: `tour name: receipt-scanning`,
    }),
    description: msg({
      message: `Learn how to quickly add items by scanning receipts`,
      context: `tour description: receipt-scanning`,
    }),
    steps: {
      'upload-receipt': {
        title: msg({
          message: `Upload a Receipt`,
          context: `receipt-scanning tour > upload-receipt: step title`,
        }),
        content: msg({
          message: `Drag and drop a receipt image or click to upload. We'll extract the items automatically.`,
          context: `receipt-scanning tour > upload-receipt: step description`,
        }),
      },
      'review-items': {
        title: msg({
          message: `Review & Confirm`,
          context: `receipt-scanning tour > review-items: step title`,
        }),
        content: msg({
          message: `Check the extracted items, adjust quantities or expiry dates, then add them all at once.`,
          context: `receipt-scanning tour > review-items: step description`,
        }),
      },
      'camera-receipt': {
        title: msg({
          message: `Snap a Receipt`,
          context: `receipt-scanning tour > camera-receipt: step title`,
        }),
        content: msg({
          message: `Take a photo of your receipt. Works best with good lighting and a flat surface.`,
          context: `receipt-scanning tour > camera-receipt: step description`,
        }),
      },
      // Mobile review-items has different content from web
      'review-items-mobile': {
        title: msg({
          message: `Quick Review`,
          context: `receipt-scanning tour > review-items-mobile: step title`,
        }),
        content: msg({
          message: `Swipe to remove items you don't want. Tap to edit details. Then hit "Add All".`,
          context: `receipt-scanning tour > review-items-mobile: step description`,
        }),
      },
    },
  },

  // =======================================================================
  // HOUSEHOLDS TOUR
  // =======================================================================
  households: {
    name: msg({ message: `Households Tour`, context: `tour name: households` }),
    description: msg({
      message: `Set up your household and learn how to manage settings`,
      context: `tour description: households`,
    }),
    steps: {
      'households-nav': {
        title: msg({
          message: `Let's Get Started`,
          context: `households tour > households-nav: step title`,
        }),
        content: msg({
          message: `First, let's set up your household. Click here to go to the Households page.`,
          context: `households tour > households-nav: step description`,
        }),
      },
      'households-intro': {
        title: msg({
          message: `What Are Households?`,
          context: `households tour > households-intro: step title`,
        }),
        content: msg({
          message: `A household groups your inventory, storage locations, and members in one place. Most people have one household for their home, but you can create more for a cottage, office, or a family member you help.`,
          context: `households tour > households-intro: step description`,
        }),
      },
      'join-or-create': {
        title: msg({
          message: `Join or Create?`,
          context: `households tour > join-or-create: step title`,
        }),
        content: msg({
          message: `Ready to create your own household, or waiting for an invite from someone else?`,
          context: `households tour > join-or-create: step description`,
        }),
        pauseLabel: msg({
          message: `I'm waiting for an invite`,
          context: `households tour > join-or-create: pause button`,
        }),
      },
      'create-household-btn': {
        title: msg({
          message: `Create a New Household`,
          context: `households tour > create-household-btn: step title`,
        }),
        content: msg({
          message: `Click here to create your first household.`,
          context: `households tour > create-household-btn: step description`,
        }),
      },
      'name-household': {
        title: msg({
          message: `Name Your Household`,
          context: `households tour > name-household: step title`,
        }),
        content: msg({
          message: `You can name your household almost anything\u2014'Home', 'The Smith Family', or whatever works for you. Type a name and click Create.`,
          context: `households tour > name-household: step description`,
        }),
      },
      'household-created': {
        title: msg({
          message: `Your Household Is Ready`,
          context: `households tour > household-created: step title`,
        }),
        content: msg({
          message: `You can explore household settings anytime using the Settings button. For now, let's add your first item.`,
          context: `households tour > household-created: step description`,
        }),
      },
      'tour-complete': {
        title: msg({
          message: `Next: Your First Item`,
          context: `households tour > tour-complete: step title`,
        }),
        content: msg({
          message: `Your household is set up. Let's head to inventory and add your first item to start tracking.`,
          context: `households tour > tour-complete: step description`,
        }),
      },
      // --- Path 2: Join (simplified) ---
      'join-info': {
        title: msg({
          message: `How to Join a Household`,
          context: `households tour > join-info: step title`,
        }),
        content: msg({
          message: `Ask your household owner to invite you from their Households page > Settings > Invite. You'll get an email with a link to join.`,
          context: `households tour > join-info: step description`,
        }),
      },
      'join-waiting': {
        title: msg({
          message: `Waiting for Your Invite`,
          context: `households tour > join-waiting: step title`,
        }),
        content: msg({
          message: `This tour will automatically resume once you join a household. If you'd rather not wait, you can always create your own household instead.`,
          context: `households tour > join-waiting: step description`,
        }),
      },
    },
  },

  // =======================================================================
  // INVENTORY ENTRY TOUR
  // =======================================================================
  'inventory-entry': {
    name: msg({
      message: `Your First Item`,
      context: `tour name: inventory-entry`,
    }),
    description: msg({
      message: `Add your first item to start tracking your pantry`,
      context: `tour description: inventory-entry`,
    }),
    steps: {
      'inventory-intro': {
        title: msg({
          message: `Your Inventory`,
          context: `inventory-entry tour > inventory-intro: step title`,
        }),
        content: msg({
          message: `This is your inventory. Let's add your first item!`,
          context: `inventory-entry tour > inventory-intro: step description`,
        }),
      },
      'add-first-item': {
        title: msg({
          message: `Add Your First Item`,
          context: `inventory-entry tour > add-first-item: step title`,
        }),
        content: msg({
          message: `Click here to add an item to your pantry.`,
          context: `inventory-entry tour > add-first-item: step description`,
        }),
      },
      'form-product': {
        title: msg({
          message: `Choose a Product`,
          context: `inventory-entry tour > form-product: step title`,
        }),
        content: msg({
          message: `Search for a product, scan a barcode, or type a custom name.`,
          context: `inventory-entry tour > form-product: step description`,
        }),
      },
      'form-expiry': {
        title: msg({
          message: `Add an Expiry Date`,
          context: `inventory-entry tour > form-expiry: step title`,
        }),
        content: msg({
          message: `Adding an expiry date helps us alert you before items go bad.`,
          context: `inventory-entry tour > form-expiry: step description`,
        }),
      },
      'form-submit': {
        title: msg({
          message: `Save Your Item`,
          context: `inventory-entry tour > form-submit: step title`,
        }),
        content: msg({
          message: `Click Save to add your first item.`,
          context: `inventory-entry tour > form-submit: step description`,
        }),
      },
      // --- Mobile steps ---
      'scan-or-search': {
        title: msg({
          message: `Scan or Search`,
          context: `inventory-entry tour > scan-or-search: step title`,
        }),
        content: msg({
          message: `Use the barcode scanner for speed, or type a product name to search.`,
          context: `inventory-entry tour > scan-or-search: step description`,
        }),
      },
      celebration: {
        title: msg({
          message: `All Set`,
          context: `inventory-entry tour > celebration: step title`,
        }),
        content: msg({
          message: `Your first item is tracked. Look for ? buttons on any page for quick mini-tours.`,
          context: `inventory-entry tour > celebration: step description`,
        }),
      },
    },
  },

  // =======================================================================
  // INVENTORY BASICS TOUR
  // =======================================================================
  'inventory-basics': {
    name: msg({ message: `Inventory Basics`, context: `tour name: inventory-basics` }),
    description: msg({
      message: `Search, filter, and review your inventory quickly`,
      context: `tour description: inventory-basics`,
    }),
    steps: {
      'inventory-search': {
        title: msg({
          message: `Search Items`,
          context: `inventory-basics tour > inventory-search: step title`,
        }),
        content: msg({
          message: `Use search to quickly find items by product name or brand.`,
          context: `inventory-basics tour > inventory-search: step description`,
        }),
      },
      'inventory-location-filter': {
        title: msg({
          message: `Filter By Location`,
          context: `inventory-basics tour > inventory-location-filter: step title`,
        }),
        content: msg({
          message: `Narrow results to a specific pantry, fridge, freezer, or other location.`,
          context: `inventory-basics tour > inventory-location-filter: step description`,
        }),
      },
      'inventory-grouped-list': {
        title: msg({
          message: `Review Inventory`,
          context: `inventory-basics tour > inventory-grouped-list: step title`,
        }),
        content: msg({
          message: `Items are grouped by storage location so you can scan what is on hand and act quickly when needed.`,
          context: `inventory-basics tour > inventory-grouped-list: step description`,
        }),
      },
      'inventory-basics-replay': {
        title: msg({
          message: `Replay Anytime`,
          context: `inventory-basics tour > inventory-basics-replay: step title`,
        }),
        content: msg({
          message: `Come back to this ? button whenever you want to revisit these basics.`,
          context: `inventory-basics tour > inventory-basics-replay: step description`,
        }),
      },
    },
  },

  // =======================================================================
  // INVENTORY ADDING TOUR
  // =======================================================================
  'inventory-adding': {
    name: msg({ message: `Adding Items`, context: `tour name: inventory-adding` }),
    description: msg({
      message: `Learn bulk add and single item entry`,
      context: `tour description: inventory-adding`,
    }),
    steps: {
      'inventory-bulk-add': {
        title: msg({
          message: `Start With Bulk Add`,
          context: `inventory-adding tour > inventory-bulk-add: step title`,
        }),
        content: msg({
          message: `Use Add Multiple when you have several products to enter at once, like after a large grocery trip.`,
          context: `inventory-adding tour > inventory-bulk-add: step description`,
        }),
      },
      'inventory-single-add': {
        title: msg({
          message: `Single Item Flow`,
          context: `inventory-adding tour > inventory-single-add: step title`,
        }),
        content: msg({
          message: `Use Add Item for quick one-off updates. Click Add Item now, then press Next.`,
          context: `inventory-adding tour > inventory-single-add: step description`,
        }),
      },
      'inventory-form-product': {
        title: msg({
          message: `Choose A Product`,
          context: `inventory-adding tour > inventory-form-product: step title`,
        }),
        content: msg({
          message: `Search for a product, scan a barcode, or enter a custom name.`,
          context: `inventory-adding tour > inventory-form-product: step description`,
        }),
      },
      'inventory-form-storage': {
        title: msg({
          message: `Set Storage Location`,
          context: `inventory-adding tour > inventory-form-storage: step title`,
        }),
        content: msg({
          message: `Pick where the item lives so your list stays organized by location.`,
          context: `inventory-adding tour > inventory-form-storage: step description`,
        }),
      },
      'inventory-form-expiry': {
        title: msg({
          message: `Add Expiry (Optional)`,
          context: `inventory-adding tour > inventory-form-expiry: step title`,
        }),
        content: msg({
          message: `Adding expiry dates improves alerts for items that need attention soon.`,
          context: `inventory-adding tour > inventory-form-expiry: step description`,
        }),
      },
      'inventory-form-submit': {
        title: msg({
          message: `Save Item`,
          context: `inventory-adding tour > inventory-form-submit: step title`,
        }),
        content: msg({
          message: `Save to add the item. Bulk add uses the same core fields, so the process will feel familiar.`,
          context: `inventory-adding tour > inventory-form-submit: step description`,
        }),
      },
    },
  },

  // =======================================================================
  // DASHBOARD DISCOVERY TOUR (steps moved from onboarding)
  // =======================================================================
  'dashboard-discovery': {
    name: msg({
      message: `Dashboard Discovery`,
      context: `tour name: dashboard-discovery`,
    }),
    description: msg({
      message: `Learn what each dashboard section does once you have items`,
      context: `tour description: dashboard-discovery`,
    }),
    steps: {
      'inventory-health': {
        title: msg({
          message: `Inventory Health`,
          context: `dashboard-discovery tour > inventory-health: step title`,
        }),
        content: msg({
          message: `This shows your pantry health at a glance. The ring fills based on how fresh your items are - green is fresh, yellow needs attention soon, red has expired.`,
          context: `dashboard-discovery tour > inventory-health: step description`,
        }),
      },
      'stats-grid': {
        title: msg({
          message: `At a Glance`,
          context: `dashboard-discovery tour > stats-grid: step title`,
        }),
        content: msg({
          message: `Quick view of your total items and how many need attention soon.`,
          context: `dashboard-discovery tour > stats-grid: step description`,
        }),
      },
      'quick-actions': {
        title: msg({
          message: `Quick Actions`,
          context: `dashboard-discovery tour > quick-actions: step title`,
        }),
        content: msg({
          message: `Fast access to the features you'll use most - add items, browse inventory, start cooking, or organize storage.`,
          context: `dashboard-discovery tour > quick-actions: step description`,
        }),
      },
      'needs-attention-list': {
        title: msg({
          message: `Needs Attention`,
          context: `dashboard-discovery tour > needs-attention-list: step title`,
        }),
        content: msg({
          message: `Items close to expiring appear here so you never waste food.`,
          context: `dashboard-discovery tour > needs-attention-list: step description`,
        }),
      },
      'expiring-group': {
        title: msg({
          message: `Item Groupings`,
          context: `dashboard-discovery tour > expiring-group: step title`,
        }),
        content: msg({
          message: `Items are grouped by urgency - expired, expiring today, or this week.`,
          context: `dashboard-discovery tour > expiring-group: step description`,
        }),
      },
      'item-actions': {
        title: msg({
          message: `Item Actions`,
          context: `dashboard-discovery tour > item-actions: step title`,
        }),
        content: msg({
          message: `Use the checkmark to mark an item as used or the X to mark it as wasted, right from this list.`,
          context: `dashboard-discovery tour > item-actions: step description`,
        }),
      },
    },
  },

  // =======================================================================
  // DASHBOARD BASICS TOUR
  // =======================================================================
  'dashboard-basics': {
    name: msg({ message: `Dashboard Basics`, context: `tour name: dashboard-basics` }),
    description: msg({
      message: `Quick walkthrough of your dashboard essentials`,
      context: `tour description: dashboard-basics`,
    }),
    steps: {
      'dashboard-welcome': {
        title: msg({
          message: `Dashboard Overview`,
          context: `dashboard-basics tour > dashboard-welcome: step title`,
        }),
        content: msg({
          message: `This is your snapshot of pantry health. It helps you spot what needs attention quickly.`,
          context: `dashboard-basics tour > dashboard-welcome: step description`,
        }),
      },
      'dashboard-inventory-health': {
        title: msg({
          message: `Inventory Health`,
          context: `dashboard-basics tour > dashboard-inventory-health: step title`,
        }),
        content: msg({
          message: `This card summarizes freshness so you can prioritize what to use first and reduce waste.`,
          context: `dashboard-basics tour > dashboard-inventory-health: step description`,
        }),
      },
      'dashboard-stats-grid': {
        title: msg({
          message: `Key Stats`,
          context: `dashboard-basics tour > dashboard-stats-grid: step title`,
        }),
        content: msg({
          message: `These counters help you track total items and urgency at a glance.`,
          context: `dashboard-basics tour > dashboard-stats-grid: step description`,
        }),
      },
      'dashboard-quick-actions': {
        title: msg({
          message: `Quick Actions`,
          context: `dashboard-basics tour > dashboard-quick-actions: step title`,
        }),
        content: msg({
          message: `Use these shortcuts to add items, jump to inventory, and manage household workflows.`,
          context: `dashboard-basics tour > dashboard-quick-actions: step description`,
        }),
      },
      'dashboard-basics-replay': {
        title: msg({
          message: `Replay Anytime`,
          context: `dashboard-basics tour > dashboard-basics-replay: step title`,
        }),
        content: msg({
          message: `Use this ? button whenever you want to quickly revisit dashboard basics.`,
          context: `dashboard-basics tour > dashboard-basics-replay: step description`,
        }),
      },
    },
  },

  // =======================================================================
  // STATISTICS BASICS TOUR
  // =======================================================================
  'statistics-basics': {
    name: msg({
      message: `Statistics Basics`,
      context: `tour name: statistics-basics`,
    }),
    description: msg({
      message: `Quick walkthrough of your product insights`,
      context: `tour description: statistics-basics`,
    }),
    steps: {
      'statistics-page-intro': {
        title: msg({
          message: `Statistics Overview`,
          context: `statistics-basics tour > statistics-page-intro: step title`,
        }),
        content: msg({
          message: `This page helps you understand purchase, consumption, waste, and spending trends over time.`,
          context: `statistics-basics tour > statistics-page-intro: step description`,
        }),
      },
      'statistics-summary': {
        title: msg({
          message: `Summary Snapshot`,
          context: `statistics-basics tour > statistics-summary: step title`,
        }),
        content: msg({
          message: `This section combines flow and totals so you can quickly spot how efficiently your pantry is being used.`,
          context: `statistics-basics tour > statistics-summary: step description`,
        }),
      },
      'statistics-products-grid': {
        title: msg({
          message: `Product Breakdown`,
          context: `statistics-basics tour > statistics-products-grid: step title`,
        }),
        content: msg({
          message: `Each card shows performance for one product, including stock, purchased, consumed, wasted, and waste rate.`,
          context: `statistics-basics tour > statistics-products-grid: step description`,
        }),
      },
      'statistics-product-details': {
        title: msg({
          message: `Open Product Details`,
          context: `statistics-basics tour > statistics-product-details: step title`,
        }),
        content: msg({
          message: `Select a product card to open deeper stats, including reason breakdowns and trend context.`,
          context: `statistics-basics tour > statistics-product-details: step description`,
        }),
      },
      'statistics-basics-replay': {
        title: msg({
          message: `Replay Anytime`,
          context: `statistics-basics tour > statistics-basics-replay: step title`,
        }),
        content: msg({
          message: `Use Help whenever you want to revisit statistics basics.`,
          context: `statistics-basics tour > statistics-basics-replay: step description`,
        }),
      },
    },
  },

  // =======================================================================
  // STATISTICS CONTROLS TOUR
  // =======================================================================
  'statistics-controls': {
    name: msg({
      message: `Statistics Controls`,
      context: `tour name: statistics-controls`,
    }),
    description: msg({
      message: `Learn timeframe and sorting controls`,
      context: `tour description: statistics-controls`,
    }),
    steps: {
      'statistics-filters': {
        title: msg({
          message: `Filters Bar`,
          context: `statistics-controls tour > statistics-filters: step title`,
        }),
        content: msg({
          message: `Use these controls to adjust which activity the page summarizes.`,
          context: `statistics-controls tour > statistics-filters: step description`,
        }),
      },
      'statistics-timeframe': {
        title: msg({
          message: `Timeframe`,
          context: `statistics-controls tour > statistics-timeframe: step title`,
        }),
        content: msg({
          message: `Switch between all-time, in-stock, or custom range to answer different inventory questions.`,
          context: `statistics-controls tour > statistics-timeframe: step description`,
        }),
      },
      'statistics-sort': {
        title: msg({
          message: `Sort & Direction`,
          context: `statistics-controls tour > statistics-sort: step title`,
        }),
        content: msg({
          message: `Sort by purchased, consumed, wasted, spending, or waste rate and flip direction for quick analysis.`,
          context: `statistics-controls tour > statistics-sort: step description`,
        }),
      },
      'statistics-product-card-controls': {
        title: msg({
          message: `Inspect Specific Products`,
          context: `statistics-controls tour > statistics-product-card-controls: step title`,
        }),
        content: msg({
          message: `After filtering and sorting, open a product card to validate why a result is high or low.`,
          context: `statistics-controls tour > statistics-product-card-controls: step description`,
        }),
      },
      'statistics-controls-replay': {
        title: msg({
          message: `Replay Anytime`,
          context: `statistics-controls tour > statistics-controls-replay: step title`,
        }),
        content: msg({
          message: `Use Help to rerun this controls walkthrough whenever you need a refresher.`,
          context: `statistics-controls tour > statistics-controls-replay: step description`,
        }),
      },
    },
  },

  // =======================================================================
  // SETTINGS BASICS TOUR
  // =======================================================================
  'settings-basics': {
    name: msg({ message: `Settings Tour`, context: `tour name: settings-basics` }),
    description: msg({
      message: `Guided walkthrough of all key settings sections`,
      context: `tour description: settings-basics`,
    }),
    steps: {
      'settings-page-intro': {
        title: msg({
          message: `Settings Overview`,
          context: `settings-basics tour > settings-page-intro: step title`,
        }),
        content: msg({
          message: `This page is where you manage profile, preferences, notifications, and account controls.`,
          context: `settings-basics tour > settings-page-intro: step description`,
        }),
      },
      'settings-profile': {
        title: msg({
          message: `Profile`,
          context: `settings-basics tour > settings-profile: step title`,
        }),
        content: msg({
          message: `Update your display name and avatar so your household members can recognize you.`,
          context: `settings-basics tour > settings-profile: step description`,
        }),
      },
      'settings-security': {
        title: msg({
          message: `Security`,
          context: `settings-basics tour > settings-security: step title`,
        }),
        content: msg({
          message: `Use this section to change your password and keep your account secure.`,
          context: `settings-basics tour > settings-security: step description`,
        }),
      },
      'settings-email': {
        title: msg({
          message: `Email Address`,
          context: `settings-basics tour > settings-email: step title`,
        }),
        content: msg({
          message: `Update your account email and complete the confirmation flow from both inboxes.`,
          context: `settings-basics tour > settings-email: step description`,
        }),
      },
      'settings-linked-accounts': {
        title: msg({
          message: `Linked Accounts`,
          context: `settings-basics tour > settings-linked-accounts: step title`,
        }),
        content: msg({
          message: `Connect or unlink sign-in providers like Apple or Google for easier access.`,
          context: `settings-basics tour > settings-linked-accounts: step description`,
        }),
      },
      'settings-preferences-section': {
        title: msg({
          message: `Preferences`,
          context: `settings-basics tour > settings-preferences-section: step title`,
        }),
        content: msg({
          message: `Choose measurement system and language defaults for your app experience.`,
          context: `settings-basics tour > settings-preferences-section: step description`,
        }),
      },
      'settings-notifications': {
        title: msg({
          message: `Notifications`,
          context: `settings-basics tour > settings-notifications: step title`,
        }),
        content: msg({
          message: `Configure reminders, quiet hours, and timing so alerts are useful without being noisy.`,
          context: `settings-basics tour > settings-notifications: step description`,
        }),
      },
      'settings-tours': {
        title: msg({
          message: `Tours Library`,
          context: `settings-basics tour > settings-tours: step title`,
        }),
        content: msg({
          message: `Use this section to start or restart guided tours whenever you want a quick feature refresher.`,
          context: `settings-basics tour > settings-tours: step description`,
        }),
      },
      'settings-danger-zone': {
        title: msg({
          message: `Danger Zone`,
          context: `settings-basics tour > settings-danger-zone: step title`,
        }),
        content: msg({
          message: `This area contains irreversible account actions. Review carefully before making changes.`,
          context: `settings-basics tour > settings-danger-zone: step description`,
        }),
      },
      'settings-basics-replay': {
        title: msg({
          message: `Replay Anytime`,
          context: `settings-basics tour > settings-basics-replay: step title`,
        }),
        content: msg({
          message: `Use Help whenever you want to revisit settings basics.`,
          context: `settings-basics tour > settings-basics-replay: step description`,
        }),
      },
    },
  },

  // =======================================================================
  // HOUSEHOLDS BASICS TOUR
  // =======================================================================
  'households-basics': {
    name: msg({
      message: `Households Basics`,
      context: `tour name: households-basics`,
    }),
    description: msg({
      message: `Quick walkthrough of the households page`,
      context: `tour description: households-basics`,
    }),
    steps: {
      'households-page-intro': {
        title: msg({
          message: `Households Page`,
          context: `households-basics tour > households-page-intro: step title`,
        }),
        content: msg({
          message: `This page is where you create and manage households for shared inventory collaboration.`,
          context: `households-basics tour > households-page-intro: step description`,
        }),
      },
      'households-create-action': {
        title: msg({
          message: `Create Household`,
          context: `households-basics tour > households-create-action: step title`,
        }),
        content: msg({
          message: `Use this button to create a household, then invite family members to collaborate together.`,
          context: `households-basics tour > households-create-action: step description`,
        }),
      },
      'households-basics-replay': {
        title: msg({
          message: `Replay Anytime`,
          context: `households-basics tour > households-basics-replay: step title`,
        }),
        content: msg({
          message: `Use this ? button whenever you want a quick households refresher.`,
          context: `households-basics tour > households-basics-replay: step description`,
        }),
      },
    },
  },

  // =======================================================================
  // HOUSEHOLDS SETTINGS BASICS TOUR
  // =======================================================================
  'households-settings-basics': {
    name: msg({
      message: `Household Settings Basics`,
      context: `tour name: households-settings-basics`,
    }),
    description: msg({
      message: `Quick walkthrough of key settings in a household`,
      context: `tour description: households-settings-basics`,
    }),
    steps: {
      'household-settings-page-intro': {
        title: msg({
          message: `Settings Overview`,
          context: `households-settings-basics tour > household-settings-page-intro: step title`,
        }),
        content: msg({
          message: `This page controls how your household is configured for all members who use it.`,
          context: `households-settings-basics tour > household-settings-page-intro: step description`,
        }),
      },
      'household-settings-name-mini': {
        title: msg({
          message: `Household Name`,
          context: `households-settings-basics tour > household-settings-name-mini: step title`,
        }),
        content: msg({
          message: `Update the household name so everyone recognizes it easily.`,
          context: `households-settings-basics tour > household-settings-name-mini: step description`,
        }),
      },
      'household-settings-region-mini': {
        title: msg({
          message: `Region`,
          context: `households-settings-basics tour > household-settings-region-mini: step title`,
        }),
        content: msg({
          message: `Region improves product results and defaults for this household across supported features.`,
          context: `households-settings-basics tour > household-settings-region-mini: step description`,
        }),
      },
      'household-settings-language-mini': {
        title: msg({
          message: `Default Language`,
          context: `households-settings-basics tour > household-settings-language-mini: step title`,
        }),
        content: msg({
          message: `Set a default product-name language for the household. Members can still override personally.`,
          context: `households-settings-basics tour > household-settings-language-mini: step description`,
        }),
      },
      'household-settings-members-mini': {
        title: msg({
          message: `Members`,
          context: `households-settings-basics tour > household-settings-members-mini: step title`,
        }),
        content: msg({
          message: `Review members and roles here. Owners and admins can invite and manage household access.`,
          context: `households-settings-basics tour > household-settings-members-mini: step description`,
        }),
      },
      'household-settings-basics-replay': {
        title: msg({
          message: `Replay Anytime`,
          context: `households-settings-basics tour > household-settings-basics-replay: step title`,
        }),
        content: msg({
          message: `Use this ? button whenever you want to revisit settings basics.`,
          context: `households-settings-basics tour > household-settings-basics-replay: step description`,
        }),
      },
    },
  },

  // =======================================================================
  // MEAL PREP BASICS TOUR
  // =======================================================================
  'meal-prep-basics': {
    name: msg({
      message: `Meal Prep Basics`,
      context: `tour name: meal-prep-basics`,
    }),
    description: msg({
      message: `Learn how to select items for meal prep`,
      context: `tour description: meal-prep-basics`,
    }),
    steps: {
      'meal-prep-page-intro': {
        title: msg({
          message: `Meal Prep Workflow`,
          context: `meal-prep-basics tour > meal-prep-page-intro: step title`,
        }),
        content: msg({
          message: `Use this page to quickly process multiple items after cooking by selecting what you used or wasted.`,
          context: `meal-prep-basics tour > meal-prep-page-intro: step description`,
        }),
      },
      'meal-prep-quick-actions': {
        title: msg({
          message: `Add Items To The Session`,
          context: `meal-prep-basics tour > meal-prep-quick-actions: step title`,
        }),
        content: msg({
          message: `Start by scanning barcodes or picking from inventory. Items you select are added to the session below.`,
          context: `meal-prep-basics tour > meal-prep-quick-actions: step description`,
        }),
      },
      'meal-prep-scanner': {
        title: msg({
          message: `Scan Barcodes`,
          context: `meal-prep-basics tour > meal-prep-scanner: step title`,
        }),
        content: msg({
          message: `Scanner mode is fastest during meal prep. It can add known items directly from inventory.`,
          context: `meal-prep-basics tour > meal-prep-scanner: step description`,
        }),
      },
      'meal-prep-picker': {
        title: msg({
          message: `Pick From Inventory`,
          context: `meal-prep-basics tour > meal-prep-picker: step title`,
        }),
        content: msg({
          message: `Use the list picker when you prefer browsing your pantry instead of scanning.`,
          context: `meal-prep-basics tour > meal-prep-picker: step description`,
        }),
      },
      'meal-prep-selected-items': {
        title: msg({
          message: `Selection Area`,
          context: `meal-prep-basics tour > meal-prep-selected-items: step title`,
        }),
        content: msg({
          message: `Selected items appear here for review before you confirm the final action.`,
          context: `meal-prep-basics tour > meal-prep-selected-items: step description`,
        }),
      },
      'meal-prep-basics-replay': {
        title: msg({
          message: `Replay Anytime`,
          context: `meal-prep-basics tour > meal-prep-basics-replay: step title`,
        }),
        content: msg({
          message: `Use this ? button whenever you want to quickly revisit meal prep basics.`,
          context: `meal-prep-basics tour > meal-prep-basics-replay: step description`,
        }),
      },
    },
  },

  // =======================================================================
  // MEAL PREP ACTIONS TOUR
  // =======================================================================
  'meal-prep-actions': {
    name: msg({
      message: `Meal Prep Actions`,
      context: `tour name: meal-prep-actions`,
    }),
    description: msg({
      message: `Understand used vs wasted flow before confirming`,
      context: `tour description: meal-prep-actions`,
    }),
    steps: {
      'meal-prep-action-bar': {
        title: msg({
          message: `Action Bar`,
          context: `meal-prep-actions tour > meal-prep-action-bar: step title`,
        }),
        content: msg({
          message: `Once items are selected, use this bar to batch process them as used or wasted.`,
          context: `meal-prep-actions tour > meal-prep-action-bar: step description`,
        }),
      },
      'meal-prep-used-action': {
        title: msg({
          message: `Mark As Used`,
          context: `meal-prep-actions tour > meal-prep-used-action: step title`,
        }),
        content: msg({
          message: `Choose Used when items were cooked, eaten, shared, or donated. You can adjust quantities in the next step.`,
          context: `meal-prep-actions tour > meal-prep-used-action: step description`,
        }),
      },
      'meal-prep-wasted-action': {
        title: msg({
          message: `Mark As Wasted`,
          context: `meal-prep-actions tour > meal-prep-wasted-action: step title`,
        }),
        content: msg({
          message: `Choose Wasted for spoilage, expiry, or damage. You will confirm quantities and a reason before submitting.`,
          context: `meal-prep-actions tour > meal-prep-wasted-action: step description`,
        }),
      },
      'meal-prep-actions-replay': {
        title: msg({
          message: `Replay Anytime`,
          context: `meal-prep-actions tour > meal-prep-actions-replay: step title`,
        }),
        content: msg({
          message: `Use this ? button whenever you want a quick reminder of meal prep actions.`,
          context: `meal-prep-actions tour > meal-prep-actions-replay: step description`,
        }),
      },
    },
  },

  // =======================================================================
  // SHOPPING LISTS TOUR
  // =======================================================================
  'shopping-lists': {
    name: msg({
      message: `Shopping Lists Tour`,
      context: `tour name: shopping-lists`,
    }),
    description: msg({
      message: `Learn how to create and share shopping lists`,
      context: `tour description: shopping-lists`,
    }),
    steps: {
      'create-list': {
        title: msg({
          message: `Create a List`,
          context: `shopping-lists tour > create-list: step title`,
        }),
        content: msg({
          message: `Start a new shopping list for your next grocery run.`,
          context: `shopping-lists tour > create-list: step description`,
        }),
      },
      'add-from-expiring': {
        title: msg({
          message: `Smart Suggestions`,
          context: `shopping-lists tour > add-from-expiring: step title`,
        }),
        content: msg({
          message: `We'll suggest items based on what you're running low on or frequently buy.`,
          context: `shopping-lists tour > add-from-expiring: step description`,
        }),
        missingElementContent: msg({
          message: `Once you have items in your pantry, we can suggest what to add to your shopping list.`,
          context: `shopping-lists tour > add-from-expiring: fallback when element missing`,
        }),
      },
      'share-list': {
        title: msg({
          message: `Share with Household`,
          context: `shopping-lists tour > share-list: step title`,
        }),
        content: msg({
          message: `Share this list with household members. Changes sync in real-time.`,
          context: `shopping-lists tour > share-list: step description`,
        }),
      },
      'shopping-tab': {
        title: msg({
          message: `Shopping Lists`,
          context: `shopping-lists tour > shopping-tab: step title`,
        }),
        content: msg({
          message: `All your shopping lists live here. Tap to create a new one.`,
          context: `shopping-lists tour > shopping-tab: step description`,
        }),
      },
      'check-items': {
        title: msg({
          message: `Check Off Items`,
          context: `shopping-lists tour > check-items: step title`,
        }),
        content: msg({
          message: `Tap items as you shop. Checked items move to the bottom automatically.`,
          context: `shopping-lists tour > check-items: step description`,
        }),
        missingElementContent: msg({
          message: `Add items to your list, then tap to check them off as you shop.`,
          context: `shopping-lists tour > check-items: fallback when element missing`,
        }),
      },
    },
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type TourField = 'name' | 'description';
type StepField = 'title' | 'content' | 'missingElementContent' | 'skipLabel' | 'pauseLabel';

/**
 * Translate a tour-level field (name or description).
 * Falls back to the provided English `fallback` if no registry entry exists.
 */
export function translateTourField(
  i18n: I18n,
  tourId: string,
  field: TourField,
  fallback: string
): string {
  const descriptor = tourMessages[tourId]?.[field];
  if (!descriptor) return fallback;
  return i18n._(descriptor) || fallback;
}

/**
 * Translate a step-level field (title, content, missingElementContent, skipLabel, pauseLabel).
 * Falls back to the provided English `fallback` if no registry entry exists.
 */
export function translateStepField(
  i18n: I18n,
  tourId: string,
  stepId: string,
  field: StepField,
  fallback: string
): string {
  const descriptor = tourMessages[tourId]?.steps[stepId]?.[field];
  if (!descriptor) return fallback;
  return i18n._(descriptor) || fallback;
}
